import { BoxProps } from '@chakra-ui/react';
import { VStack } from './stack';
import { StickyCta } from './sticky-cta';
import { ReactNode } from 'react';

// Default page column width (the "lg and up centers an 816px column" tier
// from the breakpoint spec). Pages with a narrower natural content width
// (e.g. onboarding forms using the `formWidth` token) should pass their own
// `maxWidth` so the CTA lines up with the content instead of stretching wider.
const DEFAULT_MAX_WIDTH = '816px';

type Props = {
  // pinned to the bottom of the viewport, e.g. an ArrowCta. Pass a falsy
  // value to omit the sticky footer bar entirely.
  actionArea?: ReactNode;
  // normal-flow scrollable content, between the header and the action area
  children: ReactNode;
  // pinned to the top of the viewport
  header?: ReactNode;
  // caps both the content column and the actionArea to the same width, so
  // the CTA never ends up wider than the content above it
  maxWidth?: BoxProps['maxW'];
  progressBar?: ReactNode;
  testID?: string;
};

// Sticky top header/progress bar + sticky bottom CTA, both riding the page's
// own scroll instead of a JS-measured fixed-height viewport. Works the same
// on mobile and desktop. See MobileStickyLayout for the older approach this
// is meant to replace.
export const StickyCtaLayout = ({
  actionArea,
  children,
  header,
  maxWidth = DEFAULT_MAX_WIDTH,
  progressBar,
  testID,
}: Props) => (
  <VStack minH="100dvh" w="100%" data-testid={testID}>
    {(header || progressBar) && (
      <VStack
        position="sticky"
        top={0}
        zIndex={5}
        w="100%"
        gap={0}
        bg="var(--ayble-page-bg, #eef4f6)"
      >
        {header}
        {progressBar}
      </VStack>
    )}

    <VStack flex={1} w="100%" align="center" py={6}>
      <VStack w="100%" maxW={maxWidth} px={6}>
        {children}
      </VStack>
    </VStack>

    {actionArea && (
      <StickyCta alignItems="center">
        <VStack w="100%" maxW={maxWidth} px={6} align="center">
          {actionArea}
        </VStack>
      </StickyCta>
    )}
  </VStack>
);
